/**
 * The one place that knows how a Cmd/Ctrl keyboard chord is matched.
 *
 * Three components hand-rolled this: the workspace switcher (Cmd+Shift+O), the navigation dropdown
 * (Cmd+Shift+G) and the quick-search palette (Cmd+K). The rules they each restated are here once —
 * everything ELSE about a chord stays with its own component, because it is genuinely per-component:
 * whether the shortcut is advertised, whether the element is visible (`$el.offsetParent`), and what
 * the key does.
 *
 * Matched on `event.code`, not `event.key`, so a non-US layout still reaches the shortcut — on an
 * AZERTY or Dvorak layout `event.key` is whatever character that physical key produces.
 *
 * The Mac/non-Mac arms are exclusive on purpose: requiring metaKey AND rejecting ctrlKey (and the
 * reverse elsewhere) stops Ctrl+Cmd+O from firing a Cmd+O shortcut.
 */
export function isMacPlatform() {
    return /Mac|iP/.test(navigator.platform);
}

/**
 * @param {KeyboardEvent} event
 * @param {{code: string, shift?: boolean}} chord  `code` is a KeyboardEvent.code such as 'KeyK'.
 *        `shift` is matched exactly, so a chord declared without it does not fire when Shift is held.
 * @returns {boolean}
 */
export function matchesChord(event, { code, shift = false }) {
    if (event.code !== code) {
        return false;
    }

    if (Boolean(event.shiftKey) !== Boolean(shift)) {
        return false;
    }

    return isMacPlatform()
        ? (event.metaKey && !event.ctrlKey)
        : (event.ctrlKey && !event.metaKey);
}
